import React, { useMemo, useRef, useState } from 'react';
import { Layers, Loader2, Printer } from 'lucide-react';

// Alan listesi: yayınevi alan puanlı deneme (alan_puani.js) ve Akademi tam YKS denemesi (sunucu alanPuani.yontem = 'yks').
// Liste alan alan (SAY, EA, SÖZ), alan puanına göre; toplam yalnız alanın testlerinden.
const ALAN_ORDER = ['SAY', 'EA', 'SOZ'];
const ALAN_KISA: Record<string, string> = { SAY: 'SAY', EA: 'EA', SOZ: 'SÖZ' };
const ALAN_ADI: Record<string, string> = { SAY: 'SAYISAL', EA: 'EŞİT AĞIRLIK', SOZ: 'SÖZEL' };

interface ExamResultListProps {
    // Sıra hesabının yapıldığı bütün satırlar; visibleIds verilirse yalnız onlar gösterilir/yazdırılır (şube, arama)
    data: any[];
    visibleIds?: Set<string>;
    subtitle?: string;
    metadata: any;
    examName?: string;
    examDate?: string;
    companyLogo?: string;
    companyName?: string;
}

export const ExamResultList: React.FC<ExamResultListProps> = ({ data, visibleIds, subtitle, metadata, examName, examDate, companyLogo, companyName }) => {
    const subjectColumns = useMemo(() => {
        if (!data || data.length === 0) return [];

        const testNames = metadata?.testNames || {};

        let testIds = Object.keys(testNames);
        if (testIds.length === 0 && data[0]?.answers) testIds = Object.keys(data[0].answers);

        testIds.sort((a, b) => {
            const numA = parseInt(a);
            const numB = parseInt(b);

            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.localeCompare(b);
        });

        return testIds.map(id => ({
            id,
            name: testNames[id] || `Test ${id}`
        }));
    }, [data, metadata]);

    // LGS'de 3 yanlış 1 doğruyu, YKS'de 4 yanlış 1 doğruyu götürür.
    const netPenalty = metadata?.levelType === 'lgs' ? 1 / 3 : 0.25;

    const isAlanList = useMemo(() => (data || []).some((row) => row?.results?.alanPuani?.alan), [data]);
    // Tam YKS: alan puanı sistemin YKS puanıdır, TYT puanı ayrı sütunda; AYT'ye girmeyenin alan puanı yoktur
    const isYksAlan = useMemo(() => isAlanList && (data || []).some((row) => row?.results?.alanPuani?.yontem === 'yks'), [data, isAlanList]);

    const processedData = useMemo(() => {
        let rows = data.map((student) => {
            const stats = student.stats || {};
            const answers = student.answers || {};
            const alanPuani = isAlanList ? student.results?.alanPuani : null;
            const alanTestIds: string[] = Array.isArray(alanPuani?.testIds) ? alanPuani.testIds.map(String) : [];
            // Alan dışı test listede görünür ama toplama girmez (eski kayıtta testIds yoksa hepsi sayılır)
            const inAlan = (testId: string) => !alanTestIds.length || alanTestIds.includes(String(testId));

            let totalCorrect = 0;
            let totalIncorrect = 0;
            let totalNet = 0;

            Object.entries(answers).forEach(([testId, a]: [string, any]) => {
                if (!inAlan(testId)) return;
                totalCorrect += (a.correct || 0);
                totalIncorrect += (a.incorrect || 0);

                const n = a.net !== undefined ? a.net : ((a.correct || 0) - (a.incorrect || 0) * netPenalty);
                totalNet += n;
            });

            // YKS denemesinde liste herkeste ortak olan TYT puanına göre dizilir.
            let score = stats.scoring?.sessionScores?.tytScore ?? stats.totalScore;
            if (!score || score === 0) {
                score = 100 + (totalNet * 3.4);

                if (score > 500) score = 500;
                if (score < 100) score = 100;

                if (totalNet <= 0) score = 0;
            };

            if (alanPuani) score = alanPuani.alan && alanPuani.score != null ? Number(alanPuani.score) : (isYksAlan ? null : 0);

            return {
                ...student,
                alan: alanPuani?.alan || null,
                inAlan,
                computed: {
                    totalCorrect,
                    totalIncorrect,
                    totalNet,
                    finalScore: score,
                    storedAlanRank: alanPuani?.ranks?.general?.rank ?? null,
                    tytScore: alanPuani?.tytScore ?? null,
                },
                getSubjectStats: (testId: string) => {
                    const ans = answers[testId];
                    if (!ans) return { correct: 0, incorrect: 0, net: 0 };
                    return {
                        correct: ans.correct || 0,
                        incorrect: ans.incorrect || 0,
                        net: ans.net || (ans.correct - (ans.incorrect * netPenalty))
                    };
                }
            };
        });

        const alanIndex = (row: any) => {
            const index = ALAN_ORDER.indexOf(row.alan);
            return index < 0 ? ALAN_ORDER.length : index;
        };
        const puan = (row: any) => row.computed.finalScore ?? -1;
        rows.sort((a, b) => (isAlanList ? alanIndex(a) - alanIndex(b) : 0) || puan(b) - puan(a));

        // Alan puanlı denemede genel derece alan içindedir (sunucudaki sıra: aramayla süzülen listede de bütün katılımcılara göre)
        const alanCounters: Record<string, number> = {};
        rows.forEach((r, i) => {
            if (isAlanList) {
                alanCounters[r.alan || '-'] = (alanCounters[r.alan || '-'] || 0) + 1;
                r.computed.generalRank = r.computed.finalScore == null ? null : (r.computed.storedAlanRank ?? alanCounters[r.alan || '-']);
            } else {
                r.computed.generalRank = i + 1;
            }
        });

        const branchGroups: Record<string, any[]> = {};
        rows.forEach(r => {
            const b = `${r.student?.department?.name || 'Unassigned'}${isAlanList ? `|${r.alan || '-'}` : ''}`;
            if (!branchGroups[b]) branchGroups[b] = [];
            branchGroups[b].push(r);
        });

        Object.values(branchGroups).forEach(group => {
            group.sort((a, b) => puan(b) - puan(a));
            // Eşit puan aynı derece (alan derecesiyle aynı kural); puanı olmayana derece verilmez
            group.forEach((r, i) => {
                const previous = group[i - 1];
                if (r.computed.finalScore == null) { r.computed.branchRank = null; return; }
                r.computed.branchRank = previous && previous.computed.finalScore != null && Math.abs(previous.computed.finalScore - r.computed.finalScore) < 1e-6
                    ? previous.computed.branchRank
                    : i + 1;
            });
        });

        return rows;
    }, [data, netPenalty, isAlanList, isYksAlan]);

    // Dereceler bütün katılımcılara göre hesaplandı; gösterilen (ve ortalaması alınan) satırlar süzgece göre
    const displayRows = useMemo(
        () => (visibleIds ? processedData.filter((row) => visibleIds.has(row.id)) : processedData),
        [processedData, visibleIds]
    );

    const generalAverages = useMemo(() => {
        if (!displayRows.length) return null;

        const totals: Record<string, { correct: number, incorrect: number, net: number }> = {};
        let totalCorrect = 0, totalIncorrect = 0, totalNet = 0, totalScore = 0, scoreCount = 0, totalTyt = 0, tytCount = 0;

        const subjectCounts: Record<string, number> = {};
        displayRows.forEach(row => {
            subjectColumns.forEach(sub => {
                if (!totals[sub.id]) totals[sub.id] = { correct: 0, incorrect: 0, net: 0 };
                // Alan puanlı denemede test ortalaması yalnız o testten sorumlu öğrencilerden
                if (isAlanList && !(row.alan && row.inAlan(sub.id))) return;
                const stats = row.getSubjectStats(sub.id);
                totals[sub.id].correct += stats.correct;
                totals[sub.id].incorrect += stats.incorrect;
                totals[sub.id].net += stats.net;
                subjectCounts[sub.id] = (subjectCounts[sub.id] || 0) + 1;
            });

            totalCorrect += row.computed.totalCorrect;
            totalIncorrect += row.computed.totalIncorrect;
            totalNet += row.computed.totalNet;
            if (row.computed.finalScore != null) { totalScore += row.computed.finalScore; scoreCount += 1; }
            if (row.computed.tytScore != null) { totalTyt += Number(row.computed.tytScore); tytCount += 1; }
        });

        const count = displayRows.length;
        const result: any = {
            subjects: {},
            totalCorrect: totalCorrect / count,
            totalIncorrect: totalIncorrect / count,
            totalNet: totalNet / count,
            totalScore: scoreCount ? totalScore / scoreCount : null,
            totalTyt: tytCount ? totalTyt / tytCount : null,
        };

        subjectColumns.forEach(sub => {
            const n = subjectCounts[sub.id] || 0;
            result.subjects[sub.id] = n ? {
                correct: totals[sub.id].correct / n,
                incorrect: totals[sub.id].incorrect / n,
                net: totals[sub.id].net / n
            } : null;
        });

        return result;
    }, [displayRows, subjectColumns, isAlanList]);

    // Alan puanlı denemede alan başına ortalama satırı (alanın testleri, toplam ve alan puanı)
    const statsRows = useMemo(() => {
        if (!generalAverages || !isAlanList) return [] as any[];
        return ALAN_ORDER.map((alan) => {
            const group = displayRows.filter((row) => row.alan === alan);
            if (!group.length) return null;
            const avg = (pick: (row: any) => number) => group.reduce((sum, row) => sum + pick(row), 0) / group.length;
            const tytGroup = group.filter((row) => row.computed.tytScore != null);
            const subjects: Record<string, any> = {};
            subjectColumns.forEach((sub) => {
                subjects[sub.id] = group[0].inAlan(sub.id)
                    ? {
                        correct: avg((row) => row.getSubjectStats(sub.id).correct),
                        incorrect: avg((row) => row.getSubjectStats(sub.id).incorrect),
                        net: avg((row) => row.getSubjectStats(sub.id).net),
                    }
                    : null;
            });
            return {
                label: `${ALAN_ADI[alan]} ORTALAMASI`,
                studentCount: group.length,
                subjects,
                totalCorrect: avg((row) => row.computed.totalCorrect),
                totalIncorrect: avg((row) => row.computed.totalIncorrect),
                totalNet: avg((row) => row.computed.totalNet),
                totalScore: avg((row) => row.computed.finalScore ?? 0),
                totalTyt: tytGroup.length ? tytGroup.reduce((sum, row) => sum + Number(row.computed.tytScore), 0) / tytGroup.length : null,
            };
        }).filter(Boolean) as any[];
    }, [generalAverages, subjectColumns, displayRows, isAlanList]);

    // Toplu sonuç listesini yazdır: sayfalar gizli çerçeveye uygulamanın stilleriyle kopyalanır, yatay A4 basılır
    // (uygulama düzeni overflow ile kestiği için sayfanın kendisi yazdırılmaz).
    const pagesRef = useRef<HTMLDivElement>(null);
    const [printing, setPrinting] = useState(false);
    const printList = async () => {
        const node = pagesRef.current;
        if (!node || printing) return;
        setPrinting(true);
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        document.body.appendChild(iframe);
        try {
            const doc = iframe.contentDocument;
            if (!doc) return;
            const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map((el) => el.outerHTML).join('');
            const title = String(examName || 'Sonuç Listesi').replace(/[<>&"]/g, '');
            doc.open();
            doc.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${title}</title>${styles}<style>
@page { size: A4 landscape; margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.liste-sayfa { box-shadow: none !important; overflow: visible !important; break-after: page; page-break-after: always; }
.liste-sayfa:last-child { break-after: auto; page-break-after: auto; }
.liste-yazdirma-yok { display: none !important; }
thead { display: table-header-group; }
tr { break-inside: avoid; page-break-inside: avoid; }
</style></head><body>${node.innerHTML}</body></html>`);
            doc.close();
            const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]')) as HTMLLinkElement[];
            await Promise.all(links.map((link) => (link.sheet ? null : new Promise((resolve) => {
                link.addEventListener('load', resolve);
                link.addEventListener('error', resolve);
                setTimeout(resolve, 3000);
            }))));
            await new Promise((resolve) => setTimeout(resolve, 200));
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        } finally {
            setPrinting(false);
            // Yazdırma penceresi açıkken çerçeve kaldırılırsa baskı iptal olur; geç temizlenir.
            setTimeout(() => iframe.remove(), 60000);
        }
    };

    const formatDate = (d: string) => {
        try {
            return new Date(d).toLocaleDateString('tr-TR');
        } catch {
            return d;
        }
    };

    if (!data || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Layers className="w-16 h-16 mb-4 opacity-20" />
                <p>Listelenecek veri bulunamadı.</p>
            </div>
        );
    }

    const renderHeader = (page: number, totalPages: number) => (
        <div className="mb-2 relative">
            <div className="absolute top-0 right-0 text-[10px] text-gray-600">
                Sayfa {page} / {totalPages}
            </div>

            <div className="flex items-end mb-4">
                <div className="flex flex-row items-center w-64 shrink-0 mr-4 gap-3">
                    {companyLogo ? (
                        <img src={companyLogo} alt="Logo" className="w-16 h-16 object-contain rounded-full border border-gray-200" />
                    ) : (
                        <div className="w-16 h-16 rounded-full border-2 border-black flex items-center justify-center shrink-0">
                            <span className="font-bold text-lg">DB</span>
                        </div>
                    )}
                    <div className="font-bold text-lg tracking-tight whitespace-nowrap uppercase">{companyName || 'SAYE EĞİTİM'}</div>
                </div>

                <div className="flex-1 text-center pb-2">
                    <h1 className="text-2xl font-bold text-black uppercase tracking-tight scale-y-110">{examName || 'TÜRKİYE GENELİ TYT İLK PROVA (T1225)'}</h1>
                    {subtitle && <div className="mt-1 text-sm font-bold text-black uppercase">{subtitle}</div>}
                </div>

                <div className="w-32"></div>
            </div>

            <div className="text-[11px] font-bold text-black mb-1">
                Sınav Tarihi : {formatDate(examDate || new Date().toISOString())}
            </div>
        </div>
    );

    const renderFooter = () => (
        <div className="mt-auto p-2 border-t border-gray-200 bg-gray-50 text-[10px] text-gray-500 flex justify-between items-center">
            <div>
                {companyName || 'Saye Eğitim'}
            </div>
            <div>
                {new Date().toLocaleString('tr-TR')}
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4 items-center bg-gray-100 pb-8">
            <div className="flex w-full justify-end px-4 pt-3" style={{ maxWidth: '297mm' }}>
                <button
                    type="button"
                    onClick={printList}
                    disabled={printing}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
                    title="Bütün öğrencilerin sonuç listesini yatay A4 yazdır"
                >
                    {printing ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                    Listeyi Yazdır ({displayRows.length} öğrenci)
                </button>
            </div>

            <div ref={pagesRef} className="flex flex-col gap-8 items-center">
            <div className="liste-sayfa bg-white shadow-lg flex flex-col overflow-hidden" style={{ width: '297mm', minHeight: '210mm', padding: '10mm', fontFamily: '"Arial Narrow", "Roboto Condensed", sans-serif' }}>
                {renderHeader(1, 2)}

                <div className="flex-1">
                    <table className="w-full text-center border-collapse text-[9px] min-w-max border border-black leading-none">
                        <thead>
                            <tr className="bg-[#D9E1F2]">
                                <th rowSpan={2} className="p-0.5 border border-black font-bold whitespace-nowrap text-black w-8">SIRA<br />NO</th>
                                <th rowSpan={2} className="p-0.5 border border-black font-bold whitespace-nowrap text-black w-8">ŞUBE</th>
                                {isAlanList && <th rowSpan={2} className="p-0.5 border border-black font-bold whitespace-nowrap text-black w-8">ALAN</th>}
                                <th rowSpan={2} className="p-0.5 border border-black font-bold text-left min-w-[120px] text-black px-1">ADI SOYADI</th>

                                {subjectColumns.map(sub => (
                                    <th key={sub.id} colSpan={3} className="px-0.5 py-1 border border-black font-bold text-black uppercase tracking-tight">
                                        {sub.name.toLocaleUpperCase('tr-TR')}
                                    </th>
                                ))}

                                <th colSpan={3} className="px-0.5 py-1 border border-black font-bold text-black">{isAlanList ? 'TOPLAM (ALAN)' : 'TOPLAM'}</th>
                                <th rowSpan={2} className="p-0.5 border border-black font-bold text-black w-12">{isYksAlan ? <>YKS PUANI<br /><span className="font-normal text-[7px]">SAY/EA/SÖZ</span></> : isAlanList ? <>ALAN<br />PUANI</> : <>SINAV<br />PUANI</>}</th>
                                {isYksAlan && <th rowSpan={2} className="p-0.5 border border-black font-bold text-black w-12">TYT<br />PUANI</th>}

                                <th rowSpan={2} className="p-0.5 border border-black font-bold w-6 text-[8px] text-black">ŞUBE<br />SIRASI</th>
                                <th rowSpan={2} className="p-0.5 border border-black font-bold w-6 text-[8px] text-black">{isAlanList ? <>ALAN<br />SIRASI</> : <>GENEL<br />SIRA</>}</th>
                            </tr>
                            <tr className="bg-[#D9E1F2]">
                                {subjectColumns.map(sub => (
                                    <React.Fragment key={`${sub.id}-sub`}>
                                        <th className="border border-black py-0.5 font-normal w-6">D.</th>
                                        <th className="border border-black py-0.5 font-normal w-6">Y.</th>
                                        <th className="border border-black py-0.5 font-normal w-6">N.</th>
                                    </React.Fragment>
                                ))}
                                <th className="border border-black py-0.5 font-normal w-6">D.</th>
                                <th className="border border-black py-0.5 font-normal w-6">Y.</th>
                                <th className="border border-black py-0.5 font-normal w-6">N.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayRows.map((row, idx) => {
                                const isEven = idx % 2 === 0;
                                const rowClass = isEven ? 'bg-[#DCE6F1]' : 'bg-white';
                                return (
                                    <tr key={row.id} className={rowClass}>
                                        <td className="border border-black py-0.5 font-bold text-black">{idx + 1}</td>
                                        <td className="border border-black py-0.5 text-black font-medium">{row.student?.department?.name || ''}</td>
                                        {isAlanList && <td className="border border-black py-0.5 text-black font-bold">{row.alan ? ALAN_KISA[row.alan] : '-'}</td>}
                                        <td className="border border-black py-0.5 text-left px-1 font-bold text-black uppercase whitespace-nowrap overflow-hidden max-w-[150px] text-ellipsis">
                                            {(row.student?.firstName && row.student?.lastName)
                                                ? `${row.student.firstName} ${row.student.lastName}`
                                                : (row.results?.studentInfo?.fullName || 'İsimsiz')}
                                        </td>

                                        {subjectColumns.map(sub => {
                                            const stats = row.getSubjectStats(sub.id);
                                            // Alan dışı test soluk: görünür ama toplama ve puana girmez
                                            const tone = isAlanList && !row.inAlan(sub.id) ? 'text-gray-400' : 'text-black';
                                            return (
                                                <React.Fragment key={`${row.id}-${sub.id}`}>
                                                    <td className={`border border-gray-400 border-l-black border-r-black py-0.5 ${tone}`}>{stats.correct}</td>
                                                    <td className={`border border-gray-400 border-r-black py-0.5 ${tone}`}>{stats.incorrect}</td>
                                                    <td className={`border border-black py-0.5 font-bold ${tone}`}>{Number(stats.net).toFixed(2)}</td>
                                                </React.Fragment>
                                            );
                                        })}

                                        <td className="border border-black py-0.5 font-medium text-black">{row.computed.totalCorrect}</td>
                                        <td className="border border-black py-0.5 font-medium text-black">{row.computed.totalIncorrect}</td>
                                        <td className="border border-black py-0.5 font-bold text-black">{Number(row.computed.totalNet).toFixed(2)}</td>

                                        <td className="border border-black py-0.5 font-bold text-black">{row.computed.finalScore == null ? '–' : Number(row.computed.finalScore).toFixed(3)}</td>
                                        {isYksAlan && <td className="border border-black py-0.5 text-black">{row.computed.tytScore == null ? '–' : Number(row.computed.tytScore).toFixed(3)}</td>}

                                        <td className="border border-black py-0.5 text-black">{row.computed.branchRank ?? '–'}</td>
                                        <td className="border border-black py-0.5 text-black">{row.computed.generalRank ?? '–'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {isYksAlan && (
                    <div className="mt-1 text-[8px] text-gray-600">
                        YKS puanı: öğrencinin alanındaki (SAY, EA, SÖZ) ham YKS puanıdır; TYT ve alanın iki AYT testinden hesaplanır, okul puanı (OBP) eklenmemiştir. Her öğrenci kendi alanında sıralanır, TYT puanı ayrı sütundadır. Toplam TYT ile alanın iki AYT testindendir; soluk sütunlar alan dışıdır. AYT'ye girmeyen öğrencinin YKS puanı ve alan sırası yoktur.
                    </div>
                )}
                {isAlanList && !isYksAlan && (
                    <div className="mt-1 text-[8px] text-gray-600">
                        Alan puanı: alanın iki testi 40 soruya tamamlanır (net × 40 / soru), soru başına 5 puan; 80 × 5 = 400 + 100 taban (en çok 500). Toplam alanın testlerindendir; soluk sütunlar alan dışı testtir. Alan sırası: denemeye giren aynı alandaki bütün öğrenciler arasında; şube sırası: kendi şubesinde aynı alandakiler arasında.
                    </div>
                )}
                {renderFooter()}
            </div>

            {generalAverages && (
                <div className="liste-sayfa bg-white shadow-lg flex flex-col overflow-hidden" style={{ width: '297mm', minHeight: '210mm', padding: '10mm', fontFamily: '"Arial Narrow", "Roboto Condensed", sans-serif' }}>
                    {renderHeader(2, 2)}

                    <div className="flex-1 flex flex-col justify-start pt-8">
                        <table className="w-full text-center border-collapse text-[9px] min-w-max border border-black leading-none">
                            <thead>
                                <tr className="bg-[#D9E1F2]">
                                    <th rowSpan={2} colSpan={3} className="p-0.5 border border-black font-bold text-left px-2 text-black w-64">İSTATİSTİK BİLGİLERİ</th>
                                    {subjectColumns.map(sub => (
                                        <th key={sub.id} colSpan={3} className="px-0.5 py-1 border border-black font-bold text-black uppercase tracking-tight">
                                            {sub.name.toLocaleUpperCase('tr-TR')}
                                        </th>
                                    ))}
                                    <th colSpan={3} className="px-0.5 py-1 border border-black font-bold text-black">{isAlanList ? 'TOPLAM (ALAN)' : 'TOPLAM'}</th>
                                    <th rowSpan={2} className="p-0.5 border border-black font-bold text-black w-12">{isYksAlan ? <>ORT. YKS<br />PUANI</> : <>ORT.<br />PUAN</>}</th>
                                    {isYksAlan && <th rowSpan={2} className="p-0.5 border border-black font-bold text-black w-12">ORT. TYT<br />PUANI</th>}
                                </tr>
                                <tr className="bg-[#D9E1F2]">
                                    {subjectColumns.map(sub => (
                                        <React.Fragment key={`${sub.id}-sub-2`}>
                                            <th className="border border-black py-0.5 font-normal w-6">D.</th>
                                            <th className="border border-black py-0.5 font-normal w-6">Y.</th>
                                            <th className="border border-black py-0.5 font-normal w-6">N.</th>
                                        </React.Fragment>
                                    ))}
                                    <th className="border border-black py-0.5 font-normal w-6">D.</th>
                                    <th className="border border-black py-0.5 font-normal w-6">Y.</th>
                                    <th className="border border-black py-0.5 font-normal w-6">N.</th>
                                </tr>
                            </thead>
                            <tbody>

                                <tr className="bg-[#DCE6F1]">
                                    <td colSpan={3} className="border border-black py-2 text-left px-2 text-black font-bold">{subtitle ? `${subtitle.toLocaleUpperCase('tr-TR')} ORTALAMASI` : 'GENEL ALT ORTALAMALAR'}</td>
                                    {subjectColumns.map(sub => {
                                        const avg = generalAverages.subjects[sub.id];
                                        return (
                                            <React.Fragment key={`avg-${sub.id}`}>
                                                <td className="border border-black py-2 text-black">{avg ? avg.correct.toFixed(2) : '-'}</td>
                                                <td className="border border-black py-2 text-black">{avg ? avg.incorrect.toFixed(2) : '-'}</td>
                                                <td className="border border-black py-2 text-black font-bold">{avg ? avg.net.toFixed(2) : '-'}</td>
                                            </React.Fragment>
                                        );
                                    })}
                                    <td className="border border-black py-2 text-black">{generalAverages.totalCorrect.toFixed(2)}</td>
                                    <td className="border border-black py-2 text-black">{generalAverages.totalIncorrect.toFixed(2)}</td>
                                    <td className="border border-black py-2 text-black font-bold">{generalAverages.totalNet.toFixed(2)}</td>
                                    <td className="border border-black py-2 text-black font-bold">{generalAverages.totalScore == null ? '–' : generalAverages.totalScore.toFixed(3)}</td>
                                    {isYksAlan && <td className="border border-black py-2 text-black font-bold">{generalAverages.totalTyt == null ? '–' : generalAverages.totalTyt.toFixed(3)}</td>}
                                </tr>

                                {statsRows.map((stat, idx) => (
                                    <tr key={idx} className="bg-[#FCD5B5]">
                                        <td colSpan={3} className="border border-black py-2 text-left px-2 text-black text-[9px] font-bold">
                                            {stat.label} ( {stat.studentCount} Öğrenci )
                                        </td>
                                        {subjectColumns.map(sub => {
                                            const avg = stat.subjects[sub.id];
                                            return (
                                                <React.Fragment key={`stat-${idx}-${sub.id}`}>
                                                    <td className="border border-black py-2 text-black">{avg ? avg.correct.toFixed(2) : '-'}</td>
                                                    <td className="border border-black py-2 text-black">{avg ? avg.incorrect.toFixed(2) : '-'}</td>
                                                    <td className="border border-black py-2 text-black font-bold">{avg ? avg.net.toFixed(2) : '-'}</td>
                                                </React.Fragment>
                                            );
                                        })}
                                        <td className="border border-black py-2 text-black">{stat.totalCorrect.toFixed(2)}</td>
                                        <td className="border border-black py-2 text-black">{stat.totalIncorrect.toFixed(2)}</td>
                                        <td className="border border-black py-2 text-black font-bold">{stat.totalNet.toFixed(2)}</td>
                                        <td className="border border-black py-2 text-black font-bold">{stat.totalScore.toFixed(3)}</td>
                                        {isYksAlan && <td className="border border-black py-2 text-black font-bold">{stat.totalTyt == null ? '–' : stat.totalTyt.toFixed(3)}</td>}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {renderFooter()}
                </div>
            )}
            </div>
        </div>
    );
};